<!-- @format -->
<template>
    <div class="add-phone-number-widget">
        <div class="widget-header">
            <h3>Request a Phone Number</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <div v-if="isLoading" class="loading-card">
                <loading-svg />
            </div>
            <div class="description">
                <p>
                    Request a phone number to be able to send SMS and make phone calls
                    <strong>(not available currently)</strong> from GlassHive. Some area codes are
                    incapable of having all options.
                </p>
            </div>
            <div class="form-container">
                <div class="phone-container">
                    <label>User</label>
                    <v-select
                        :items="items"
                        class="app-select no-padding"
                        item-text="label"
                        item-value="value"
                        outlined
                        rounded
                        single-line
                        v-model="userId"
                    >
                        <div slot="item" slot-scope="data" class="user-item">
                            <div class="user-thumbnail">
                                <img
                                    :src="
                                        data.item.label.image
                                            ? data.item.label.image
                                            : require('@/assets/images/avatar.png')
                                    "
                                    alt="User"
                                />
                            </div>

                            <span>{{ data.item.label.text }}</span>
                        </div>

                        <div slot="selection" slot-scope="data" class="user-item">
                            <div class="user-thumbnail">
                                <img
                                    :src="
                                        data.item.label.image
                                            ? data.item.label.image
                                            : require('@/assets/images/avatar.png')
                                    "
                                    alt="User"
                                />
                            </div>

                            <span>{{ data.item.label.text }}</span>
                        </div>
                    </v-select>
                </div>
                <div class="country-container">
                    <label>Country Code</label>

                    <country-selector
                        :id="`id_country_selector`"
                        ref="CountrySelector"
                        v-model="countryCode"
                        hint="Choose country"
                        class="input-country-selector"
                        :items="codesCountries"
                        :show-code-on-list="true"
                        :ignored-countries="ignoredCountries"
                    />
                </div>
                <div class="preferred-area-code-container">
                    <app-input v-model="preferredAreaCode" label="Preferred Area Code (Optional)" />
                </div>
                <div class="checkboxs-container">
                    <label>What capabilities are required for this phone number?</label>
                    <label class="checkbox-label">
                        <app-checkbox v-model="isSmsCapable" />
                        <span>SMS</span>
                    </label>
                    <label class="checkbox-label">
                        <app-checkbox v-model="isMmsCapable" />
                        <span>MMS</span>
                    </label>
                    <label class="checkbox-label">
                        <app-checkbox v-model="isVoiceCapable" />
                        <span>Voice</span>
                    </label>
                </div>
            </div>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="cancel-btn transparent-btn" @click="closeWidget">
                    Cancel
                </app-button>
            </div>
            <div class="action-container">
                <app-button class="save-btn" @click="handleCreatePhoneNumber"> Next </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const CountrySelector = () =>
    import('@/components/phone-country-input/country-selector/country-selector');
const AppInput = () => import('@/components/common/app-input');
const LoadingSvg = () => import('@/components/svg/loading-screen-svg');
const AppCheckbox = () => import('@/components/common/app-checkbox');

import {
    countries,
    countriesIso,
} from '@/components/phone-country-input/assets/js/phoneCodeCountries.js';
import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'add-phone-number-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'country-selector': CountrySelector,
        'app-input': AppInput,
        'loading-svg': LoadingSvg,
        'app-checkbox': AppCheckbox,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            items: [],
            userId: null,
            countryCode: 'US',
            ignoredCountries: [],
            preferredAreaCode: '',
            isSmsCapable: true,
            isMmsCapable: true,
            isVoiceCapable: true,
        };
    },

    async mounted() {
        const result = await this.$api.get(`/service/Users?$filter=ClientId eq ${this.clientId}`);

        this.items = result.data.value.map((item) => ({
            label: { text: `${item.FirstName} ${item.LastName}`, image: item.ProfileImageUrl },
            value: item.Id,
        }));

        if (this.items[0]) {
            this.userId = this.items[0].value;
        }
    },

    computed: {
        accountId() {
            return this.$store.state.context.account.id;
        },

        clientId() {
            return this.$store.state.context.client.id;
        },

        codesCountries() {
            return countries.filter(
                (c) => c.iso2 === 'CA' || c.iso2 === 'US' || c.iso2 === 'MX' || c.iso2 === 'NL'
            );
        },
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        async handleCreatePhoneNumber() {
            try {
                this.isLoading = true;
                await this.$api.post(`/api/PhoneNumbers`, {
                    PreferredAreaCode: this.preferredAreaCode,
                    IsVoiceCapable: this.isVoiceCapable,
                    IsSmsCapable: this.isSmsCapable,
                    IsMmsCapable: this.isMmsCapable,
                    UserId: this.userId,
                    CountryCode: this.countryCode,
                    AccountId: this.accountId,
                });
                EventBus.$emit('phone-number:update');
                this.closeWidget();
            } catch (error) {
                this.$helpers.showFailNotification(
                    error.response.data.Message.includes('area code')
                        ? 'Unable to generate phone number using the preferred area code.'
                        : 'An error occurred trying to create the phone number.',
                    { error },
                );
            } finally {
                this.isLoading = false;
            }
        },
    },
};
</script>

<style>
.add-phone-number-widget .cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.add-phone-number-widget .cancel-btn:hover {
    background: #e1e0e5;
}

.add-phone-number-widget .content-wrapper .loading-card {
    position: absolute;
    display: flex;
    justify-content: center;
    top: 0;
    left: 0;
    width: 100%;
    align-items: center;
    background: #00000025;
    border-radius: 0 20px 20px 0;
    height: 100%;
    z-index: 1;
}

.add-phone-number-widget .content-wrapper .description {
    margin-bottom: 30px;
}

.add-phone-number-widget .content-wrapper .description h3 {
    font: 700 18px/24px 'Nunito', sans-serif;
    color: #382153;
}

.add-phone-number-widget .content-wrapper .description p {
    font: normal 16px/24px 'Roboto', sans-serif;
    color: #382153;
}

.add-phone-number-widget .content-wrapper .app-border-section .top-text {
    font: 18px 'Roboto', sans-serif;
    font-weight: 500;
}

.add-phone-number-widget .form-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.add-phone-number-widget .form-container label {
    font-weight: 500;
}

.add-phone-number-widget .verification-code-container span {
    margin-top: 10px;
    display: inline-block;
    color: #382153;
    font-size: 16px;
}

.add-phone-number-widget .action-container {
    display: flex;
    gap: 19px;
}

.user-thumbnail {
    height: 32px;
    width: 32px;
    border-radius: 50%;
    overflow: hidden;
}

.user-item {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 4px 8px;
}

.add-phone-number-widget .v-input__prepend-inner {
    margin: 0px !important;
}

.no-padding .v-input__slot {
    padding-left: 0px !important;
}

.add-phone-number-widget .preferred-area-code-container .app-input {
    margin: 0px;
    width: 100%;
}

.add-phone-number-widget .country-container .country-selector {
    max-width: 100%;
}

.add-phone-number-widget .checkboxs-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.add-phone-number-widget .checkboxs-container .checkbox-label {
    display: flex;
    gap: 8px;
    font-weight: 400 !important;
}
</style>
